// src/components/auth/UpdatePasswordForm.tsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock, Eye, EyeOff, CheckCircle, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { supabase } from '@/utils/supabase';
import { analytics } from '@/utils/analytics';

export const UpdatePasswordForm: React.FC = () => {
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const [checkingSession, setCheckingSession] = useState(true);

  useEffect(() => {
    let isMounted = true; // Flag to prevent state updates on unmounted component

    // Log URL details on mount
    console.log('UpdatePasswordForm mounted.');
    console.log('Current URL:', window.location.href);
    console.log('URL Hash:', window.location.hash); // IMPORTANT: Check this in your browser console

    const handleAuthChange = async (event, session) => {
      if (!isMounted) return;

      console.log('Supabase Auth State Change Event:', event);
      console.log('Supabase Auth State Change Session:', session);

      if (event === 'PASSWORD_RECOVERY' || event === 'SIGNED_IN') {
        if (session) {
          console.log('Session found via auth change event! User ID:', session.user.id);
          setIsReady(true);
          setError(null);
        } else {
          console.log('No session found after auth change event, or session is null.');
          setError('Lien de réinitialisation invalide ou expiré. Veuillez demander un nouveau lien.');
        }
        setCheckingSession(false);
      } else if (event === 'SIGNED_OUT') {
        console.log('User signed out during password recovery flow.');
        setIsReady(false);
        setError('Session terminée. Veuillez demander un nouveau lien de réinitialisation.');
        setCheckingSession(false);
      } else {
        console.log('Unhandled Auth State Change Event:', event);
      }
    };

    // Initial check for session on component mount
    const initialCheck = async () => {
      if (!isMounted) return;

      console.log('Performing initial session check with supabase.auth.getSession()...');
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();

      if (sessionError) {
        console.error('Error getting session initially:', sessionError);
        setError('Erreur lors de la vérification de la session. Veuillez réessayer.');
        setCheckingSession(false);
        return;
      }

      if (session) {
        console.log('Initial session found! User ID:', session.user.id);
        setIsReady(true);
        setError(null);
      } else {
        console.log('No initial session found. Waiting for onAuthStateChange or showing error.');
        // If no session found initially, and no specific PASSWORD_RECOVERY event fires,
        // the error will be set by the default path or if onAuthStateChange doesn't provide one.
        // We keep checkingSession true here to allow onAuthStateChange to potentially fire.
        // If onAuthStateChange doesn't fire a relevant event, checkingSession will remain true
        // and the component will be stuck. Let's add a timeout for this case.
        setTimeout(() => {
          if (isMounted && checkingSession) { // Only set error if still checking and no session found
            console.log('Timeout reached, no session established.');
            setError('Lien de réinitialisation invalide ou expiré. Veuillez demander un nouveau lien.');
            setCheckingSession(false);
          }
        }, 3000); // Give it 3 seconds to establish session via hash
      }
    };

    initialCheck();

    // Subscribe to auth state changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(handleAuthChange);

    return () => {
      isMounted = false;
      subscription.unsubscribe();
      console.log('UpdatePasswordForm unmounted.');
    };
  }, []); // Empty dependency array means this runs once on mount and cleanup on unmount

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (password.length < 6) {
      setError('Le mot de passe doit contenir au moins 6 caractères');
      return;
    }

    if (password !== confirmPassword) {
      setError('Les mots de passe ne correspondent pas');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const { error: updateError } = await supabase.auth.updateUser({
        password: password
      });

      if (updateError) {
        throw updateError;
      }

      setSuccess(true);

      // Redirection après 3 secondes
      setTimeout(() => {
        navigate('/auth?mode=login');
      }, 3000);

    } catch (err: any) {
      console.error('Erreur:', err);
      setError(err.message || 'Erreur lors de la mise à jour du mot de passe');
    } finally {
      setLoading(false);
    }
  };

  if (checkingSession) {
    return (
      <div className="w-full max-w-md mx-auto">
        <div className="bg-white rounded-lg shadow-lg p-8 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Vérification du lien de réinitialisation...</p>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="w-full max-w-md mx-auto">
        <div className="bg-white rounded-lg shadow-lg p-8 text-center">
          <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Mot de passe mis à jour !</h2>
          <p className="text-gray-600 mb-6">
            Votre mot de passe a été mis à jour avec succès. Vous allez être redirigé vers la page de connexion...
          </p>
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-md mx-auto">
      <div className="bg-white rounded-lg shadow-lg p-8">
        <div className="text-center mb-8">
          <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Lock className="w-6 h-6 text-blue-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Nouveau mot de passe</h1>
          <p className="text-gray-600">
            Définissez votre nouveau mot de passe
          </p>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
            <p className="text-red-700 text-sm">{error}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="relative">
            <Input
              id="password"
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Minimum 6 caractères"
              label="Nouveau mot de passe"
              leftIcon={<Lock className="w-5 h-5" />}
              required
              disabled={loading || !isReady}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-[38px] transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              tabIndex={-1}
            >
              {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
            </button>
          </div>

          <div className="relative">
            <Input
              id="confirmPassword"
              type={showConfirmPassword ? 'text' : 'password'}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Confirmer le mot de passe"
              label="Confirmer le mot de passe"
              leftIcon={<Lock className="w-5 h-5" />}
              required
              disabled={loading || !isReady}
            />
            <button
              type="button"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              className="absolute right-3 top-[38px] transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              tabIndex={-1}
            >
              {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
            </button>
          </div>

          <Button
            type="submit"
            variant="primary"
            size="lg"
            loading={loading}
            className="w-full"
            disabled={!password || !confirmPassword || !isReady}
          >
            Mettre à jour le mot de passe
          </Button>
        </form>

        <div className="mt-6 text-center">
          <button
            onClick={() => navigate('/auth?mode=login')}
            className="text-blue-600 hover:text-blue-700 font-medium text-sm"
            disabled={loading}
          >
            Retour à la connexion
          </button>
        </div>
      </div>
    </div>
  );
};
